import {
  ActionRef,
  ActionStatus,
  ActionTransactionSubTypes,
  ActionCategory,
} from 'further-types/lp/action';

import { IncomeAndExpensesLabels } from './income-and-expenses';
import { InvestmentTypeLabels } from './investment';
import {
  CurrencySaleRevaluationTypeLabels,
  BankReconciliationActionTypeLabels,
} from './bank-reconciliation';
import { RevaluationTypeLabels } from './revaluation';
import { FeeActionTypeLabels } from 'further-types/lp/fee-actions';
import { RealisationTypeLabels } from './realisation';
import { UnrealisedFXGainOrLossActionTypeLabels } from 'further-types/lp/unrealised-fx-gain-or-loss';
import { CapitalActionTypeLabels } from './capital-action';
import {
  UnrealisedCashFXGainOrLossActionTypeLabels,
  UnrealisedAssetFXGainOrLossTypeLabels,
} from './unrealised-fx-gain-or-loss';
import { PartnerTransferTypeLabels } from './partner-transfer';

export const ActionRefLabels = {
  [ActionRef.CapitalAction]: 'Capital Action',
  [ActionRef.IncomeOrExpense]: 'Income or Expense',
  [ActionRef.Investment]: 'Investment',
  [ActionRef.Realisation]: 'Realisation',
  [ActionRef.Revaluation]: 'Revaluation',
  [ActionRef.Reconciliation]: 'Reconciliation',
  [ActionRef.BankAccountTransfer]: 'Bank Transfer',
  [ActionRef.Fees]: 'Fees',
  [ActionRef.PrepaymentResolution]: 'Prepayment Resolution',
  [ActionRef.UnrealisedFXGainOrLoss]: 'Unrealised FX Gain or Loss',
  [ActionRef.RealisedFXGainOrLoss]: 'Realised FX Gain or Loss',
  [ActionRef.UnrealisedCashFXGainOrLoss]: 'Unrealised Cash FX Gain or Loss',
  [ActionRef.ForeignCurrencySaleGainOrLoss]:
    'Foreign Currency Sale Gain or Loss',
  [ActionRef.UnrealisedAssetFXGainOrLoss]: 'Unrealised Asset FX Gain or Loss',
  [ActionRef.PartnerTransfer]: 'Partner Transfer',
};

export const ActionStatusLabels: Record<ActionStatus, string> = {
  [ActionStatus.Draft]: 'Draft',
  [ActionStatus.Approved]: 'Approved',
  [ActionStatus.Rejected]: 'Rejected',
};

export const ActionCategoryLabels: Record<ActionCategory, string> = {
  [ActionCategory.Income]: 'Income',
  [ActionCategory.Expense]: 'Expense',
  [ActionCategory.Investment]: 'Investment',
  [ActionCategory.Realisation]: 'Realisation',
  [ActionCategory.ManagementFees]: 'Management Fees',
  [ActionCategory.CapitalActions]: 'Capital Actions',
  [ActionCategory.FX]: 'FX',
};

export const ActionTypeLabels = {
  ...IncomeAndExpensesLabels,
  ...InvestmentTypeLabels,
  ...CurrencySaleRevaluationTypeLabels,
  ...RevaluationTypeLabels,
  ...FeeActionTypeLabels,
  ...RealisationTypeLabels,
  ...UnrealisedFXGainOrLossActionTypeLabels,
  ...UnrealisedCashFXGainOrLossActionTypeLabels,
  ...UnrealisedAssetFXGainOrLossTypeLabels,
  ...BankReconciliationActionTypeLabels,
  ...CapitalActionTypeLabels,
  ...PartnerTransferTypeLabels,
};

export const ActionTransactionSubTypeLabels = {
  [ActionTransactionSubTypes.CreditDebitPair]: 'Credit/Debit pair',
  [ActionTransactionSubTypes.CashProceeds]: 'Cash proceeds',
  [ActionTransactionSubTypes.EscrowProceeds]: 'Escrow proceeds',
  [ActionTransactionSubTypes.WithholdingTax]: 'Withholding tax',
  [ActionTransactionSubTypes.RealisedGainFX]: 'Realised gain FX',
  [ActionTransactionSubTypes.RealisedGain]: 'Realised gain',
  [ActionTransactionSubTypes.RealisedLossFX]: 'Realised loss FX',
  [ActionTransactionSubTypes.RealisedLoss]: 'Realised loss',
  [ActionTransactionSubTypes.SaleOfInvestment]: 'Sale of investment',
  [ActionTransactionSubTypes.UnrealisedGainsUnwind]: 'Unrealised gains unwind',
  [ActionTransactionSubTypes.UnrealisedLossesUnwind]:
    'Unrealised losses unwind',
  [ActionTransactionSubTypes.FXGainPayable]: 'Fx Gain on payable',
  [ActionTransactionSubTypes.FXLossPayable]: 'Fx Loss on payable',
  [ActionTransactionSubTypes.FXGainReceivable]: 'Fx Gain on receivable',
  [ActionTransactionSubTypes.FXLossReceivable]: 'Fx Loss on receivable',
  [ActionTransactionSubTypes.ExpensesCall]: 'Expenses call',
  [ActionTransactionSubTypes.InvestmentCall]: 'Investment call',
  [ActionTransactionSubTypes.ShareCapitalCall]: 'Share capital call',
  [ActionTransactionSubTypes.GeneralCapitalCall]: 'General capital call',
  [ActionTransactionSubTypes.FeesCall]: 'Fees call',
  [ActionTransactionSubTypes.CapitalRefund]: 'Capital refund',
  [ActionTransactionSubTypes.RecallableReturnOfCapital]:
    'Recallable return of capital',
  [ActionTransactionSubTypes.NonRecallableReturnOfCapital]:
    'Non-recallable return of capital',
  [ActionTransactionSubTypes.RecallableIncome]: 'Recallable income',
  [ActionTransactionSubTypes.NonRecallableIncome]: 'Non-recallable income',
  [ActionTransactionSubTypes.RecallableDistributionOutsideOfCarry]:
    'Recallable distribution outside of carry',
  [ActionTransactionSubTypes.NonRecallableDistributionOutsideOfCarry]:
    'Non-recallable distribution outside of carry',
  [ActionTransactionSubTypes.CarriedInterest]: 'Carried interest',
  [ActionTransactionSubTypes.CallReceivable]: 'Call receivable',
  [ActionTransactionSubTypes.DistributionsPayable]: 'Distributions payable',
  [ActionTransactionSubTypes.EqualisationCall]: 'Equalisation call',
  [ActionTransactionSubTypes.EqualisationRefund]: 'Equalisation refund',
  [ActionTransactionSubTypes.EqualisationInterestPaidIn]:
    'Equalisation interest paid in',
  [ActionTransactionSubTypes.EqualisationInterestPaidOut]:
    'Equalisation interest paid out',
  [ActionTransactionSubTypes.CarriedInterestPayable]:
    'Carried interest payable',
  [ActionTransactionSubTypes.RealisedFXGain]: 'Realised FX gain',
  [ActionTransactionSubTypes.RealisedFXLoss]: 'Realised FX loss',
  [ActionTransactionSubTypes.UnrealisedCashFXGain]: 'Unrealised Cash FX Gain',
  [ActionTransactionSubTypes.UnrealisedCashFXLoss]: 'Unrealised Cash FX Loss',
};
